import { useTheme, Button } from '@crackpoint/shared/components';
import type { HintWithMetadata } from '@crackpoint/shared/types';
import { strings } from '../../config';
import { HintCard } from './HintCard';
import { cn } from '@crackpoint/shared/utils';

interface HintDeckProps {
  hints: HintWithMetadata[];
  hintsRemaining: number;
  isLoading?: boolean;
  onDrawHint?: () => void;
  className?: string;
  emptyMessage?: string;
  drawButtonText?: string;
}

export function HintDeck({
  hints,
  hintsRemaining,
  isLoading = false,
  onDrawHint,
  className,
  emptyMessage = strings.hints.emptyMessage,
  drawButtonText = strings.hints.drawButton,
}: HintDeckProps) {
  const { theme } = useTheme();

  return (
    <div
      className={cn('hint-deck', className)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div
        className="hint-deck__header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: theme.spacing.md,
          backgroundColor: theme.colours.background.secondary,
          borderTop: `1px solid ${theme.colours.background.tertiary}`,
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: theme.typography.fontSize.md,
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.colours.text.primary,
            }}
          >
            {strings.hints.title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colours.text.secondary,
            }}
          >
            {strings.hints.drawnRemaining(hints.length, hintsRemaining)}
          </p>
        </div>

        {onDrawHint && (
          <Button
            size="sm"
            onClick={onDrawHint}
            disabled={hintsRemaining === 0 || isLoading}
            loading={isLoading}
          >
            {drawButtonText}
          </Button>
        )}
      </div>

      <div
        className="hint-deck__content"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: theme.spacing.md,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md,
          backgroundColor: theme.colours.background.primary,
        }}
      >
        {hints.length === 0 ? (
          <div
            className="hint-deck__empty"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: theme.typography.fontSize.md,
                color: theme.colours.text.tertiary,
              }}
            >
              {emptyMessage}
            </p>
          </div>
        ) : (
          hints.map((hint, index) => (
            <HintCard
              key={hint.id}
              hint={hint}
              index={index}
              isNew={hint.is_new}
            />
          ))
        )}
      </div>

      {hintsRemaining > 0 && (
        <div
          className="hint-deck__progress"
          style={{
            padding: theme.spacing.sm,
            backgroundColor: theme.colours.background.secondary,
            borderTop: `1px solid ${theme.colours.background.tertiary}`,
          }}
        >
          <div
            style={{
              height: '4px',
              backgroundColor: theme.colours.background.tertiary,
              borderRadius: theme.borders.radius.full,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${(hints.length / (hints.length + hintsRemaining)) * 100}%`,
                backgroundColor: theme.colours.accent.primary,
                transition: `width ${theme.transitions.normal}`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface HintDeckCompactProps {
  hints: HintWithMetadata[];
  hintsRemaining: number;
  onDrawHint?: () => void;
  isLoading?: boolean;
  className?: string;
}

export function HintDeckCompact({
  hints,
  hintsRemaining,
  onDrawHint,
  isLoading = false,
  className,
}: HintDeckCompactProps) {
  const { theme } = useTheme();

  return (
    <div
      className={cn('hint-deck-compact', className)}
      style={{
        backgroundColor: theme.colours.background.secondary,
        borderRadius: theme.borders.radius.md,
        padding: theme.spacing.md,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: hints.length > 0 ? theme.spacing.md : 0,
        }}
      >
        <div>
          <span
            style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colours.text.secondary,
            }}
          >
            {strings.hints.compactCount(hints.length, hints.length + hintsRemaining)}
          </span>
        </div>
        {onDrawHint && (
          <button
            onClick={onDrawHint}
            disabled={hintsRemaining === 0 || isLoading}
            style={{
              backgroundColor: hintsRemaining > 0 ? theme.colours.accent.primary : theme.colours.background.tertiary,
              color: hintsRemaining > 0 ? theme.colours.text.inverse : theme.colours.text.tertiary,
              border: 'none',
              borderRadius: theme.borders.radius.sm,
              padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              cursor: hintsRemaining > 0 ? 'pointer' : 'not-allowed',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading ? '...' : '+'}
          </button>
        )}
      </div>

      {hints.length > 0 && (
        <div
          style={{
            maxHeight: '200px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing.sm,
          }}
        >
          {hints.slice(-3).reverse().map((hint) => (
            <div
              key={hint.id}
              style={{
                display: 'flex',
                gap: theme.spacing.xs,
                fontSize: theme.typography.fontSize.sm,
              }}
            >
              <span style={{ color: theme.colours.text.tertiary }}>
                [{hint.digits.join(' ')}]
              </span>
              <span style={{ color: theme.colours.text.primary }}>
                {hint.correct_placed} placed, {hint.correct_misplaced} misplaced
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
